import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Loader2,
  Plus,
  Trash2,
  ExternalLink,
  Share2,
  Layers
} from 'lucide-react';
import { getPlatformIcon as getPlatformIconUtil } from '../../utils/platformIcons';
import moment from 'moment';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter,
  DialogTrigger
} from "../../components/ui/dialog";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";

interface MonitoredAccount {
  id: string;
  handle: string;
  platform: string;
  twitterId?: string;
  isManual: boolean;
  createdAt: string;
  lastFetchedAt?: string;
}

export default function MonitoredAccounts() {
  const token = localStorage.getItem('token');
  const queryClient = useQueryClient();
  const [isBulkAddOpen, setIsBulkAddOpen] = useState(false);
  const [bulkPlatform, setBulkPlatform] = useState('twitter');
  const [bulkHandles, setBulkHandles] = useState('');

  const { data: accounts, isLoading, error } = useQuery<MonitoredAccount[]>({
    queryKey: ['admin-monitored-accounts'],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/admin/monitored-accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    enabled: !!token
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${API_BASE_URL}/admin/monitored-accounts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-monitored-accounts'] });
      toast.success("Account removed from monitoring");
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || "Failed to delete account");
    }
  });

  const bulkAddMutation = useMutation({
    mutationFn: async (data: { platform: string, handles: string }) => {
      const response = await axios.post(`${API_BASE_URL}/admin/monitored-accounts/bulk`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-monitored-accounts'] });
      toast.success(data.message || "Bulk import successful");
      setIsBulkAddOpen(false);
      setBulkHandles('');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || "Failed to import accounts");
    }
  });

  const handleBulkAdd = () => {
    if (!bulkHandles.trim()) {
      toast.error("Please enter at least one handle");
      return;
    }
    bulkAddMutation.mutate({ platform: bulkPlatform, handles: bulkHandles });
  };

  const getPlatformIcon = (platform: string) => getPlatformIconUtil(platform, 'h-3 w-3');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-black text-foreground flex items-center gap-2">
            <Share2 className="h-6 w-6 text-primary" />
            Monitored Accounts
          </h2>
          <p className="text-muted-foreground text-sm">Influencers and accounts used for trend discovery.</p>
        </div>

        <Dialog open={isBulkAddOpen} onOpenChange={setIsBulkAddOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90 text-white font-black gap-2 px-6 shadow-lg shadow-primary/20">
              <Plus className="h-4 w-4" />
              Bulk Add
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border text-foreground sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-xl font-black flex items-center gap-2">
                <Layers className="h-5 w-5 text-primary" />
                Bulk Add Influencers
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                Add multiple accounts at once. Paste one handle per line.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Platform</Label>
                <Select value={bulkPlatform} onValueChange={setBulkPlatform}>
                  <SelectTrigger className="bg-muted border-border">
                    <SelectValue placeholder="Select platform" />
                  </SelectTrigger>
                  <SelectContent className="bg-muted border-border text-foreground">
                    <SelectItem value="twitter">X (Twitter)</SelectItem>
                    <SelectItem value="tiktok">TikTok</SelectItem>
                    <SelectItem value="bluesky">Bluesky</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Handles (One per line)</Label>
                <Textarea 
                  placeholder={"@username\nanother_handle\n..."} 
                  className="min-h-[200px] bg-muted border-border font-mono text-sm"
                  value={bulkHandles}
                  onChange={(e) => setBulkHandles(e.target.value)}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setIsBulkAddOpen(false)} disabled={bulkAddMutation.isPending}>
                Cancel
              </Button>
              <Button 
                onClick={handleBulkAdd} 
                className="bg-primary hover:bg-primary/90 text-white font-black"
                disabled={bulkAddMutation.isPending}
              >
                {bulkAddMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Import Accounts
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-card border-border text-foreground shadow-2xl overflow-hidden">
        <Table>
          <TableHeader className="bg-card/50">
            <TableRow className="hover:bg-transparent border-border">
              <TableHead className="text-muted-foreground font-bold uppercase tracking-wider text-[10px]">Handle</TableHead>
              <TableHead className="text-muted-foreground font-bold uppercase tracking-wider text-[10px]">Platform</TableHead>
              <TableHead className="text-muted-foreground font-bold uppercase tracking-wider text-[10px]">Added</TableHead>
              <TableHead className="text-muted-foreground font-bold uppercase tracking-wider text-[10px]">Last Fetch</TableHead>
              <TableHead className="text-right text-muted-foreground font-bold uppercase tracking-wider text-[10px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {accounts?.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-10 text-muted-foreground italic">
                  No accounts are being monitored yet.
                </TableCell>
              </TableRow>
            )}
            {accounts?.map((account) => (
              <TableRow key={account.id} className="border-border hover:bg-card/30 transition-colors">
                <TableCell className="font-bold text-foreground flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-card flex items-center justify-center text-[10px]">
                    {account.handle.substring(0, 2).toUpperCase()}
                  </div>
                  {account.handle.startsWith('@') ? account.handle : `@${account.handle}`}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="bg-muted border-border text-foreground gap-1.5 py-1 px-2.5">
                    {getPlatformIcon(account.platform)}
                    <span className="capitalize">{account.platform}</span>
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground text-xs">
                  <div className="flex flex-col">
                    <span>{moment(account.createdAt).format('LL')}</span>
                    {account.isManual && <span className="text-[9px] text-primary/70 font-black uppercase">Manual Add</span>}
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground text-xs italic">
                  {account.lastFetchedAt ? moment(account.lastFetchedAt).fromNow() : 'Never'}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    {account.platform === 'twitter' && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" asChild>
                        <a href={`https://x.com/${account.handle}`} target="_blank" rel="noreferrer">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 text-muted-foreground hover:text-red-400"
                      onClick={() => {
                        if (window.confirm(`Stop monitoring ${account.handle.startsWith('@') ? account.handle : `@${account.handle}`}?`)) {
                          deleteMutation.mutate(account.id);
                        }
                      }}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
